import React, { useState } from 'react';
import { Box } from '@mui/material';
import ImageCarousel from 'components/atomic/molecules/image-carousel';
import { containerSizes } from 'design-system/tokens';

/**
 * GalleryCarousel Props
 */
export interface GalleryCarouselProps {
  /** Array of image URLs */
  images: string[];

  /** Alt text prefix */
  altPrefix: string;

  /** Image descriptions for accessibility (Map of index -> description) */
  imageDescriptions?: Map<string, string> | null;

  /** Overlay elements to display (e.g., badges) */
  overlay?: React.ReactNode;

  /** Counter element to display (receives current index and total) */
  renderCounter?: (current: number, total: number) => React.ReactNode;

  /** Height of the gallery */
  height?: number | string;

  /** Called when slide changes */
  onSlideChange?: (index: number) => void;

  /** Called when gallery is clicked */
  onGalleryClick?: () => void;
}

/**
 * GalleryCarousel Component
 *
 * Mobile-friendly swipeable image gallery.
 * Counter is passed via renderCounter prop to maintain layer separation.
 *
 * @example
 * ```tsx
 * <GalleryCarousel
 *   images={['img1.jpg', 'img2.jpg']}
 *   altPrefix="Property images"
 *   overlay={<BadgeGroup />}
 *   renderCounter={(current, total) => (
 *     <ImageCounter current={current} total={total} variant="mobile" />
 *   )}
 *   onGalleryClick={() => openFullGallery()}
 * />
 * ```
 */
export function GalleryCarousel({
  images,
  altPrefix,
  imageDescriptions,
  overlay,
  renderCounter,
  height = containerSizes.gallery.mobile,
  onSlideChange,
  onGalleryClick,
}: GalleryCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleSlideChange = (index: number) => {
    setCurrentIndex(index);
    onSlideChange?.(index);
  };

  return (
    <ImageCarousel
      images={images}
      altPrefix={altPrefix}
      imageDescriptions={imageDescriptions}
      height={height}
      priority={true}
      onSlideChange={handleSlideChange}
      onImageClick={onGalleryClick}
    >
      {overlay}
      {renderCounter && (
      <Box sx={{ position: 'absolute', bottom: 16, right: 16, zIndex: 2 }}>
          {renderCounter(currentIndex + 1, images.length)}
      </Box>
      )}
    </ImageCarousel>
  );
}

export default GalleryCarousel;
